import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  HouseIcon,
  ChartBarIcon,
  UsersThreeIcon,
  ListBulletsIcon,
  WrenchIcon,
  ClipboardTextIcon,
  PackageIcon,
  BellIcon,
  UsersIcon,
  GaugeIcon,
} from "@phosphor-icons/react";

import { useAuth } from "../../contexts/AuthContext";
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  countResults,
  fetchGlobalSearch,
  pathFor,
} from "../../lib/search";

const RESULT_ICONS = {
  customers: UsersIcon,
  properties: HouseIcon,
  work_orders: WrenchIcon,
  service_requests: ClipboardTextIcon,
  rma_records: PackageIcon,
};

/**
 * Build the role-aware quick-action list. Returns array of:
 *   { key, label, hint, Icon, group, onSelect }
 *
 * Owner/ops see CREATE actions; techs see navigation only. Per Phase 15
 * spec, "Go to Monitoring" is available to all roles.
 */
function buildActions(role, nav) {
  const isWriter = role === "owner" || role === "ops";

  const create = isWriter
    ? [
        {
          key: "new-customer",
          label: "New Customer",
          hint: "Open create modal",
          Icon: UsersIcon,
          group: "Create",
          onSelect: () => nav("/customers?new=1"),
        },
        {
          key: "new-sr",
          label: "New Service Request",
          hint: "Open create modal",
          Icon: ClipboardTextIcon,
          group: "Create",
          onSelect: () => nav("/service-requests?new=1"),
        },
        {
          key: "log-alert",
          label: "Log Alert",
          hint: "Open monitoring log-alert",
          Icon: BellIcon,
          group: "Create",
          onSelect: () => nav("/monitoring?new=alert"),
        },
        {
          key: "new-rma",
          label: "New RMA",
          hint: "Open RMA create modal",
          Icon: PackageIcon,
          group: "Create",
          onSelect: () => nav("/rma-records?new=1"),
        },
      ]
    : [];

  const navigate = [
    {
      key: "go-dashboard",
      label: "Go to Dashboard",
      hint: "Open",
      Icon: GaugeIcon,
      group: "Navigate",
      onSelect: () => nav("/"),
    },
    {
      key: "go-monitoring",
      label: "Go to Monitoring",
      hint: "Open",
      Icon: BellIcon,
      group: "Navigate",
      onSelect: () => nav("/monitoring"),
    },
  ];

  if (role === "owner") {
    navigate.push(
      {
        key: "go-reports",
        label: "Go to Reports",
        hint: "Open",
        Icon: ChartBarIcon,
        group: "Navigate",
        onSelect: () => nav("/reports"),
      },
      {
        key: "go-team",
        label: "Go to Team",
        hint: "Open",
        Icon: UsersThreeIcon,
        group: "Navigate",
        onSelect: () => nav("/team"),
      },
      {
        key: "go-audit",
        label: "Go to Audit Log",
        hint: "Open",
        Icon: ListBulletsIcon,
        group: "Navigate",
        onSelect: () => nav("/audit-log"),
      }
    );
  }

  return [...create, ...navigate];
}

/**
 * Command palette modal (Cmd+K / Ctrl+K).
 * Renders quick actions when no query, search results when typing.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 */
export default function CommandPalette({ open, onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  const canSearch = user?.role === "owner" || user?.role === "ops";

  const actions = useMemo(() => buildActions(user?.role, nav), [user?.role, nav]);

  // Focus input on open + reset state on close.
  useEffect(() => {
    if (open) {
      setQ("");
      setResults(null);
      setHighlight(0);
      // Defer to next tick — input not yet in DOM until <div> mounts.
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Search debounce.
  useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (!canSearch || trimmed.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await fetchGlobalSearch(trimmed);
        setResults(data);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, canSearch]);

  // Build the linear list of selectable items based on mode.
  const flatItems = useMemo(() => {
    const trimmed = q.trim();
    if (trimmed.length >= 2 && results) {
      const out = [];
      for (const cat of CATEGORY_ORDER) {
        for (const r of results[cat] || []) {
          out.push({
            key: `${cat}-${r.id}`,
            kind: "result",
            cat,
            id: r.id,
            label: r.label,
            sublabel: r.sublabel,
            Icon: RESULT_ICONS[cat],
            group: CATEGORY_LABELS[cat],
            hint: "Open",
            onSelect: () => {
              nav(pathFor(cat, r.id));
              onClose();
            },
          });
        }
      }
      return out;
    }
    // Default: quick actions, optionally filtered by typed text (<2 chars)
    if (trimmed.length === 0) return actions;
    const lc = trimmed.toLowerCase();
    return actions.filter((a) => a.label.toLowerCase().includes(lc));
  }, [q, results, actions, nav, onClose]);

  // Reset highlight when item list changes.
  useEffect(() => {
    setHighlight(0);
  }, [flatItems.length]);

  // Keyboard navigation.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, flatItems.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = flatItems[highlight];
        if (item) {
          item.onSelect();
          if (item.kind !== "result") onClose();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, flatItems, highlight]);

  if (!open) return null;

  // Group items in render order.
  const groups = [];
  let lastGroup = null;
  for (const item of flatItems) {
    if (item.group !== lastGroup) {
      groups.push({ name: item.group, items: [] });
      lastGroup = item.group;
    }
    groups[groups.length - 1].items.push(item);
  }

  return (
    <div
      data-testid="cmd-palette"
      className="fixed inset-0 z-[100] flex items-start justify-center pt-24"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative z-10 w-full max-w-xl rounded-lg border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
          <MagnifyingGlassIcon size={16} weight="bold" className="text-slate-400" />
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={
              canSearch
                ? "Type a command or search… (Esc to close)"
                : "Type a command… (Esc to close)"
            }
            data-testid="cmd-palette-input"
            className="flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
          <kbd className="font-data rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-500">
            Esc
          </kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 text-xs text-slate-500">Searching…</div>
          )}
          {!loading && flatItems.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              {q.trim().length >= 2
                ? `No results for "${q.trim()}"`
                : "No actions available"}
            </div>
          )}
          {!loading &&
            groups.map((g) => (
              <div key={g.name} className="border-b border-slate-100 last:border-b-0">
                <div className="font-data px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  {g.name}
                </div>
                <ul>
                  {g.items.map((item) => {
                    const idx = flatItems.indexOf(item);
                    const active = idx === highlight;
                    const Icon = item.Icon || PlusIcon;
                    return (
                      <li key={item.key}>
                        <button
                          type="button"
                          onMouseEnter={() => setHighlight(idx)}
                          onClick={() => {
                            item.onSelect();
                            if (item.kind !== "result") onClose();
                          }}
                          data-testid={`cmd-palette-item-${item.key}`}
                          className={`flex w-full items-center gap-3 px-4 py-2 text-left transition ${
                            active ? "bg-amber-50" : "hover:bg-slate-50"
                          }`}
                        >
                          <Icon
                            size={14}
                            weight="bold"
                            className={`shrink-0 ${
                              active ? "text-amber-600" : "text-slate-400"
                            }`}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-slate-900">
                              {item.label}
                            </span>
                            {item.sublabel && (
                              <span className="block truncate text-xs text-slate-500">
                                {item.sublabel}
                              </span>
                            )}
                          </span>
                          <span className="shrink-0 inline-flex items-center gap-1 font-data text-[10px] uppercase tracking-wide text-slate-400">
                            <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5">
                              ↵
                            </kbd>
                            {item.hint}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
